import { CaretDownIcon, CaretLeftIcon, CaretRightIcon, CaretUpIcon, CheckIcon } from '@phosphor-icons/react'
import { typography } from '@pixee/design-system'
import { Label } from '@radix-ui/react-label'
import * as Select from '@radix-ui/react-select'
import classnames from 'classnames'
import React from 'react'
import ChevronBarLeftIcon from '../icons/chevron-bar-left.svg?react'
import ChevronBarRightIcon from '../icons/chevron-bar-right.svg?react'
import * as styles from './pagination-controls.css.ts'

export type PaginationControlsProps = {
  tableName: string
  pageIndex: number
  pageSize: number
  totalItems: number
  onPageIndexChange: (pageIndex: number) => unknown
  onPageSizeChange: (pageSize: number) => unknown
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 200] as const

/**
 * @param tableName Label for the table, used in aria-label
 * @param pageIndex Current page index (zero indexed)
 * @param pageSize Current number of items per page
 * @param totalItems Total number of items across all pages
 * @param onPageIndexChange Callback when page index changes
 * @param onPageSizeChange Callback when page size changes
 */
export const PaginationControls: React.FC<PaginationControlsProps> = ({
  tableName,
  pageIndex,
  pageSize,
  totalItems,
  onPageIndexChange,
  onPageSizeChange,
}) => {
  if (totalItems === 0) return <></>

  const { rowMin, rowMax, isPreviousPageButtonActive, isNextPageButtonActive } = calculatePaginationControls(
    pageSize,
    totalItems,
    pageIndex
  )

  const onFirstPage = () => onPageIndexChange(0)
  const onPreviousPage = () => onPageIndexChange(pageIndex - 1)
  const onNextPage = () => onPageIndexChange(pageIndex + 1)
  const onLastPage = () => onPageIndexChange(Math.ceil(totalItems / pageSize) - 1)
  const onChangePageSize = (pageSize: string) => {
    onFirstPage()
    onPageSizeChange(Number(pageSize))
  }

  return (
    <nav aria-label={`${tableName} Table Pagination Navigation`} className={styles.paginationControls}>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'baseline' }}>
        <button aria-label={'Go to First Page'} onClick={onFirstPage} disabled={!isPreviousPageButtonActive}>
          <ChevronBarLeftIcon className={styles.endChevron[isPreviousPageButtonActive ? 'active' : 'disabled']} />
        </button>
        <button onClick={onPreviousPage} disabled={!isPreviousPageButtonActive} aria-label={`Go to Previous Page`}>
          <CaretLeftIcon
            className={styles.baseCaret[isPreviousPageButtonActive ? 'active' : 'disabled']}
            size={16}
            weight="light"
          />
        </button>
        <span className={typography.smallBold}>
          {rowMin}-{rowMax}
        </span>{' '}
        <span className={typography.extraSmall}>of</span> <span className={typography.small}>{totalItems}</span>
        <button onClick={onNextPage} disabled={!isNextPageButtonActive} aria-label={'Go to Next Page'}>
          <CaretRightIcon
            className={styles.baseCaret[isNextPageButtonActive ? 'active' : 'disabled']}
            size={16}
            weight="light"
          />
        </button>
        <button aria-label={'Go to Last Page'} onClick={onLastPage} disabled={!isNextPageButtonActive}>
          <ChevronBarRightIcon className={styles.endChevron[isNextPageButtonActive ? 'active' : 'disabled']} />
        </button>
      </div>
      <div>
        <Select.Root defaultValue={String(pageSize)} onValueChange={onChangePageSize}>
          <Select.Trigger
            className={styles.selectTrigger}
            aria-label={`Select ${tableName} Page Size`}
            id={`${tableName.toLowerCase()}-page-size-select`}
          >
            <Select.Value />
            <Select.Icon>
              <CaretDownIcon className={styles.caretDown} size={16} weight="fill" />
              <CaretUpIcon className={styles.caretUp} size={16} weight="fill" />
            </Select.Icon>
          </Select.Trigger>
          <Select.Content
            className={styles.selectContent}
            onCloseAutoFocus={e => {
              e.preventDefault()
            }}
            position="popper"
            side="bottom"
            /**
             * Collision avoidance left on, so the list flips above the trigger when there is no room
             * below it.
             *
             * It used to be off, which meant a control near the bottom of a long page opened its options
             * past the fold with no way to reach them — `200` was unclickable on a 120-row estate in a
             * 720px viewport. Downward is still preferred; this only changes what happens when down does
             * not fit.
             */
            collisionPadding={8}
          >
            {PAGE_SIZE_OPTIONS.map(option => (
              <SelectItem key={option} value={String(option)} text={String(option)} />
            ))}
          </Select.Content>
        </Select.Root>
        <Label
          className={styles.label}
          aria-label={`${pageSize} per page`}
          htmlFor={`${tableName.toLowerCase()}-page-size-select`}
        >
          per page
        </Label>
      </div>
    </nav>
  )
}

type SelectItemProps = {
  ref?: React.Ref<HTMLDivElement>
  children?: React.ReactNode
  text: string
  className?: string
  value: string
  disabled?: boolean
}

const SelectItem: React.FC<SelectItemProps> = ({ ref, children, text, className, value, ...props }) => (
  <Select.Item className={classnames(styles.selectItem, className)} value={value} {...props} ref={ref}>
    {children}
    <Select.ItemText>{text}</Select.ItemText>
    <Select.ItemIndicator className={styles.selectItemIndicator}>
      <CheckIcon className={styles.checkIcon} size={16} weight="light" />
    </Select.ItemIndicator>
  </Select.Item>
)

export const calculatePaginationControls = (pageSize: number, totalItems: number, pageIndex: number) => ({
  totalPages: Math.ceil(totalItems / pageSize) - 1,
  rowMin: (pageIndex + 1) * pageSize - pageSize + 1,
  rowMax: Math.min((pageIndex + 1) * pageSize, totalItems),
  isPreviousPageButtonActive: pageIndex > 0,
  isNextPageButtonActive: pageIndex < Math.ceil(totalItems / pageSize) - 1,
})
